import { Box, Divider, Skeleton, Text, useToast } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React from 'react';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { ReceivableBorrowingBaseTestWaiverGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  formatBorrowerVerificationReportJsonToBorrowingBaseReport,
  getEarliestValidReferenceBorrowingBaseReport,
} from './utils';
import { VerificationApproveDenyAction } from './VerificationApproveDenyAction';
import { VerificationApproveTablesStickyHeader } from './VerificationApproveTablesStickyHeader';
import { VerificationFundingRequestTable } from './VerificationFundingRequestTable';
import { VerificationNotifyReportReadyToReviewAction } from './VerificationNotifyReportReadyToReviewAction';
import { VerificationReportWaiverImpact } from './VerificationReportWaiverImpact';
import { VerificationApproveTableHeader } from './VerificationTableSharedComponents';
import { VerificationApproveTablesMode } from './verificationTablesUtils';
import {
  useGetBorrowingBaseReportQuery,
  useGetBorrowingBaseReportsQuery,
} from 'packages/borrowing-base/requests/borrowingBase.gql.generated';
import { BorrowingBaseSummaryWithAllValuesAndDifference } from 'packages/borrowing-base/ui/borrowingBaseSummary/BorrowingBaseSummaryWithAllValuesAndDifference';
import { BorrowingBaseSummarySections } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import {
  getMockUiVisibilityMode,
  useCreditFacilityUiVisibility,
} from 'packages/common-frontend/hooks/useUiVisibility';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

type VerificationApproveTablesContentProps = {
  creditFacilityId: string;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  selectedReportingDate: Util.FullDateStr;
  fileVersion?: number; // will not be present in funding request history view
  verificationReport: Util.SanitizedJSON;
  verificationReportId: string;
  verificationReportBorrowingBaseReportId: string;
  approvalReportNote?: string;
  artifactDocumentId?: string; // will not be present in funding request history view
  allReceivableWaivers?: ReceivableBorrowingBaseTestWaiverGQLType[];
  viewMode: VerificationApproveTablesMode;
};
const VerificationApproveTablesContent = ({
  creditFacilityId,
  creditAgreement,
  selectedReportingDate,
  fileVersion,
  verificationReport,
  verificationReportId,
  verificationReportBorrowingBaseReportId,
  approvalReportNote,
  artifactDocumentId,
  allReceivableWaivers,
  viewMode,
}: VerificationApproveTablesContentProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const mockUiVisibilityMode = getMockUiVisibilityMode({
    flags,
    defaultTo: 'lenderOwnerWithoutBorrower',
  });
  const facilityUiVisibility = useCreditFacilityUiVisibility({
    mockUiVisibilityMode,
    creditFacilityId,
  });

  const weekBeforeReportingDate = DateTime.fromISO(selectedReportingDate)
    .minus({ weeks: 1 })
    .toISODate() as Util.FullDateStr;

  const {
    data: borrowingBaseReportsData,
    loading: borrowingBaseReportsLoading,
  } = useGetBorrowingBaseReportsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      startReportingDate: weekBeforeReportingDate,
    },
  });

  const {
    data: verificationBorrowingBaseReportData,
    loading: isVerificationBorrowingBaseReportLoading,
  } = useGetBorrowingBaseReportQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      borrowingBaseReportId: verificationReportBorrowingBaseReportId,
    },
  });

  if (
    !borrowingBaseReportsData ||
    borrowingBaseReportsLoading ||
    !verificationBorrowingBaseReportData ||
    isVerificationBorrowingBaseReportLoading
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="verification-approve-tables-loading"
      />
    );
  }

  const borrowingBaseReports =
    borrowingBaseReportsData.borrowingBaseReports ?? [];

  const verificationReportFinleyAgentBorrowingBaseReport =
    verificationBorrowingBaseReportData.borrowingBaseReport;

  finleyAssert(
    verificationReportFinleyAgentBorrowingBaseReport,
    `Finley agent borrowing base report must exist for verification for [verificationReportId: ${verificationReportId}] and [borrowingBaseReportId: ${verificationReportBorrowingBaseReportId}] `,
  );

  const verificationReportBorrowerParsedBorrowingBaseReport = formatBorrowerVerificationReportJsonToBorrowingBaseReport(
    { verificationReport, creditAgreement },
  );

  // getBorrowingBaseReports is ordered by desc reportingDate, fileVer
  // @dani verification - if we want the latest fileVer of the earliest reportingDate, reorganize
  const earliestBorrowingBaseReportFromPreviousWeek = getEarliestValidReferenceBorrowingBaseReport(
    { borrowingBaseReports, invalidReportingDate: selectedReportingDate },
  );
  const priorWeekFinalDate = earliestBorrowingBaseReportFromPreviousWeek
    ? commonDate.fullDateStrToSlashedDateStr(
        earliestBorrowingBaseReportFromPreviousWeek.reportingDate,
        commonDate.formatSlashedDateStr,
      )
    : 'N/A';

  const slashedReportingDate = commonDate.fullDateStrToSlashedDateStr(
    selectedReportingDate,
    commonDate.formatSlashedDateStr,
  );

  const isReceivableEligibilityDifferencesLinkAddressAvailable = Boolean(
    artifactDocumentId &&
      creditFacilityId &&
      selectedReportingDate &&
      fileVersion &&
      verificationReportId,
  );
  const receivableEligibilityDifferencesLinkAddress = isReceivableEligibilityDifferencesLinkAddressAvailable
    ? `/funding-requests/verifications/pipeline/${artifactDocumentId}/${creditFacilityId}/${selectedReportingDate}/${fileVersion}/${verificationReportId}/eligibility`
    : undefined;

  const concentrationTrendsLinkAddress = `/portfolio/borrowing-base?${creditFacilityIdQueryParam}=${creditFacilityId}&display=concentration`;

  const capitalProviderNames = creditAgreement.facilityMechanics.parties.capitalProviders.map(
    (capitalProviderEntity) => capitalProviderEntity.entity.name,
  );
  const borrowerOrganizationName =
    creditAgreement.facilityMechanics.parties.borrowers[0].entity.name; // only one borrower per credit agreement

  return (
    // maxH required for table top sticky header to work
    // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
    <Box
      borderRadius="8px"
      border={`1px solid ${colors.gray[200]}`}
      backgroundColor="white"
      p="24px"
    >
      <Box
        maxHeight="90vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
      >
        <VerificationApproveTablesStickyHeader
          borrowingBaseReportsDates={{
            minuendValue: slashedReportingDate,
            subtrahendValue: slashedReportingDate,
            referenceValue: priorWeekFinalDate,
          }}
        />
        <BorrowingBaseSummaryWithAllValuesAndDifference
          sections={[
            BorrowingBaseSummarySections.facilitySummary,
            BorrowingBaseSummarySections.receivables,
            BorrowingBaseSummarySections.cash,
            BorrowingBaseSummarySections.finalBorrowingBaseReport,
          ]}
          borrowingBaseReports={{
            minuend: verificationReportFinleyAgentBorrowingBaseReport,
            subtrahend: verificationReportBorrowerParsedBorrowingBaseReport,
            reference: earliestBorrowingBaseReportFromPreviousWeek,
          }}
          creditAgreement={creditAgreement}
          eligibilityLink={
            receivableEligibilityDifferencesLinkAddress
              ? {
                  linkAddress: receivableEligibilityDifferencesLinkAddress,
                  linkLabel: 'See Receivable Eligibility Differences & Waivers',
                }
              : undefined
          }
          concentrationLink={
            facilityUiVisibility.verification
              .showConcentrationLimitsInsightsLink
              ? {
                  linkAddress: concentrationTrendsLinkAddress,
                  linkLabel: 'View Concentration Limit Trends',
                }
              : undefined
          }
        />
        <Box mt="32px">
          <VerificationApproveTableHeader title="Funding Request" />
          <VerificationFundingRequestTable
            creditFacilityId={creditFacilityId}
            verificationReport={verificationReport}
            reportingDate={selectedReportingDate}
            viewMode={viewMode}
            verificationReportFinleyAgentBorrowingBaseReport={
              verificationReportFinleyAgentBorrowingBaseReport
            }
          />
        </Box>
      </Box>

      {artifactDocumentId && fileVersion && (
        <VerificationReportWaiverImpact
          creditFacilityId={creditFacilityId}
          reportingDate={selectedReportingDate}
          verificationReportId={verificationReportId}
          calcAgentMechanics={
            creditAgreement.borrowingBaseMechanics.calcAgentMechanics
          }
          borrowingBaseReport={verificationReportFinleyAgentBorrowingBaseReport}
          allReceivableWaivers={allReceivableWaivers}
          borrowerOrganizationName={borrowerOrganizationName}
        />
      )}
      <Divider opacity="100%" mt="40px" mb="8px" />
      {viewMode === 'approveDeny' &&
        facilityUiVisibility?.verification.showAdjudicationActions && (
          <VerificationApproveDenyAction
            verificationReportId={verificationReportId}
            creditFacilityId={creditFacilityId}
          />
        )}
      {viewMode === 'approveDeny' &&
        facilityUiVisibility?.verification.showNotifyReportReadyAction && (
          <VerificationNotifyReportReadyToReviewAction
            verificationReportId={verificationReportId}
            capitalProviderNames={capitalProviderNames}
          />
        )}
      {viewMode === 'viewOnly' && (
        <Box>
          <Box h="32px" />
          <VerificationApproveTableHeader title="Notes" />
          <Box h="16px" />
          <Text color={colors.gray[500]}>{approvalReportNote}</Text>
        </Box>
      )}
    </Box>
  );
};

type VerificationApproveTablesProps = {
  creditFacilityId: string;
  selectedReportingDate: Util.FullDateStr;
  fileVersion?: number; // will not be present in funding request history view
  verificationReport: Util.SanitizedJSON;
  verificationReportId: string;
  verificationReportBorrowingBaseReportId: string;
  approvalReportNote?: string;
  artifactDocumentId?: string; // will not be present in funding request history view
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  allReceivableWaivers?: ReceivableBorrowingBaseTestWaiverGQLType[];
  viewMode: VerificationApproveTablesMode;
};
export const VerificationApproveTables = ({
  creditFacilityId,
  selectedReportingDate,
  fileVersion,
  verificationReport,
  verificationReportId,
  verificationReportBorrowingBaseReportId,
  approvalReportNote,
  artifactDocumentId,
  creditAgreement,
  allReceivableWaivers,
  viewMode,
}: VerificationApproveTablesProps) => {
  return (
    <VerificationApproveTablesContent
      creditAgreement={creditAgreement}
      creditFacilityId={creditFacilityId}
      selectedReportingDate={selectedReportingDate}
      fileVersion={fileVersion}
      verificationReport={verificationReport}
      verificationReportId={verificationReportId}
      verificationReportBorrowingBaseReportId={
        verificationReportBorrowingBaseReportId
      }
      approvalReportNote={approvalReportNote}
      artifactDocumentId={artifactDocumentId}
      allReceivableWaivers={allReceivableWaivers}
      viewMode={viewMode}
    />
  );
};
